import {
  SIEM_PROTOCOL_VALUES,
  WEBHOOK_DESTINATION_LOG_FORMAT,
} from 'containers/Settings/Alerts/constants'

import { STATUS } from './constants'

const BACKEND_AUTH_METHOD_HEADER = 'HEADER'
const LEADING_BEARER = /^bearer\s+/i

const AUTH_METHODS = {
  NONE: 'None',
  CUSTOM_HEADER: 'Custom header',
  BEARER: 'Bearer Token',
}

export function apiStatusToUi(status) {
  if (status === 'deactivated' || status === 'disabled') return STATUS.DISABLED
  if (status === 'draft') return STATUS.DRAFT
  if (status === 'error') return STATUS.ERROR
  return STATUS.ACTIVE
}

export function uiStatusToApi(status) {
  if (status === STATUS.DISABLED) return 'deactivated'
  if (status === STATUS.DRAFT) return 'draft'
  return 'active'
}

function webhookFieldsFromProtocolConfiguration(pc = {}) {
  const authHeaderName = pc.authHeaderName || ''
  const authHeaderValue = pc.authHeaderValue || ''

  if (
    authHeaderName === 'Authorization' &&
    typeof authHeaderValue === 'string' &&
    authHeaderValue.startsWith('Bearer ')
  ) {
    return {
      authMethod: AUTH_METHODS.BEARER,
      token: authHeaderValue.slice(7),
    }
  }

  if (authHeaderName && authHeaderValue) {
    return {
      authMethod: AUTH_METHODS.CUSTOM_HEADER,
      headerName: authHeaderName,
      headerValue: authHeaderValue,
    }
  }

  return { authMethod: AUTH_METHODS.NONE }
}

function webhookProtocolConfiguration(fields = {}) {
  const authMethod = fields.authMethod || AUTH_METHODS.NONE
  const url = fields.target?.trim() || null

  if (authMethod === AUTH_METHODS.CUSTOM_HEADER) {
    return {
      kind: 'webhook',
      url,
      authMethod: BACKEND_AUTH_METHOD_HEADER,
      authHeaderName: fields.headerName?.trim() || null,
      authHeaderValue: fields.headerValue || null,
    }
  }

  if (authMethod === AUTH_METHODS.BEARER) {
    const token = fields.token?.trim().replace(LEADING_BEARER, '').trim()
    return {
      kind: 'webhook',
      url,
      authMethod: BACKEND_AUTH_METHOD_HEADER,
      authHeaderName: token ? 'Authorization' : null,
      authHeaderValue: token ? `Bearer ${token}` : null,
    }
  }

  return { kind: 'webhook', url, authMethod: null, authHeaderName: null, authHeaderValue: null }
}

export function buildDiagnosticDestinationPayload({ name, fields }) {
  return {
    dataModelVersion: 'V2',
    name: name?.trim() || undefined,
    protocol: SIEM_PROTOCOL_VALUES['Generic Webhook (HTTP)'],
    logFormat: WEBHOOK_DESTINATION_LOG_FORMAT,
    protocolConfiguration: webhookProtocolConfiguration(fields),
  }
}

export function destinationToEndpoint(destination) {
  const pc = destination?.protocolConfiguration
  const webhook = pc?.kind === 'webhook' ? pc : {}
  const fields = {
    target: webhook.url || '',
    format: 'JSON',
    ...webhookFieldsFromProtocolConfiguration(webhook),
  }

  return {
    id: destination.id,
    name: destination.name || '',
    status: apiStatusToUi(destination.status),
    target: fields.target,
    format: 'JSON',
    connectedRuleIds: destination.ruleIds || [],
    fields,
    apiDestination: destination,
  }
}

export function createdDestinationToEndpoint(createResponse, formEndpoint) {
  const { protocolConfiguration } = endpointFormToCreatePayload(formEndpoint)
  return destinationToEndpoint({ ...createResponse, protocolConfiguration })
}

export function endpointFormToCreatePayload(endpoint) {
  return buildDiagnosticDestinationPayload({
    name: endpoint.name,
    fields: endpoint.fields,
  })
}

export function endpointFormToUpdatePayload(endpoint) {
  const status = uiStatusToApi(endpoint.status)
  const { dataModelVersion: _fixedAtCreation, ...connection } = buildDiagnosticDestinationPayload({
    name: endpoint.name,
    fields: endpoint.fields,
  })
  return {
    ...connection,
    status,
    ...(Array.isArray(endpoint.connectedRuleIds) ? { ruleIds: endpoint.connectedRuleIds } : {}),
  }
}

export function endpointFormToActivatePayload(formEndpoint) {
  return endpointFormToUpdatePayload({ ...formEndpoint, status: STATUS.ACTIVE })
}

export function endpointStatusTogglePayload(_apiDestination, currentUiStatus, targetStatus) {
  if (targetStatus) return { status: uiStatusToApi(targetStatus) }
  const isLive = currentUiStatus === STATUS.ACTIVE || currentUiStatus === STATUS.ERROR
  return { status: uiStatusToApi(isLive ? STATUS.DISABLED : STATUS.ACTIVE) }
}
